import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import GoogleMapComponent from '../../../components/GoogleMapComponent';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { geocodeAddress, reverseGeocode, getCurrentLocation, isGoogleMapsConfigured } from '../../../services/googleMapsService';

const LocationTab = ({ formData, onChange, validationErrors, onBlur }) => {
  const [isGeocoding, setIsGeocoding] = React.useState(false);
  const [geocodeError, setGeocodeError] = React.useState('');
  const [isGettingLocation, setIsGettingLocation] = React.useState(false);

  // Rename validationErrors prop to errors for consistency with usage in the component
  const errors = validationErrors;

  const cities = [
    { value: 'amman', label: 'عمان' },
    { value: 'zarqa', label: 'الزرقاء' },
    { value: 'irbid', label: 'إربد' },
    { value: 'aqaba', label: 'العقبة' },
    { value: 'salt', label: 'السلط' },
    { value: 'madaba', label: 'مادبا' },
    { value: 'jerash', label: 'جرش' },
    { value: 'ajloun', label: 'عجلون' },
    { value: 'karak', label: 'الكرك' },
    { value: 'mafraq', label: 'المفرق' }
  ];

  const handleLocationSelect = (position) => {
    onChange('latitude', position?.lat?.toString());
    onChange('longitude', position?.lng?.toString());
    
    // Reverse geocode to get address details
    if (isGoogleMapsConfigured()) {
      reverseGeocode(position?.lat, position?.lng)?.then(result => {
          if (result?.address && !formData?.address) {
            onChange('address', result?.address);
          }
          if (result?.neighborhood && !formData?.neighborhood) {
            onChange('neighborhood', result?.neighborhood);
          }
        })?.catch(error => {
          console.error('Reverse geocoding error:', error);
        });
    }
  };

  const handleGeocodeAddress = async () => {
    if (!formData?.address) {
      setGeocodeError('الرجاء إدخال العنوان أولاً');
      return;
    }

    setIsGeocoding(true);
    setGeocodeError('');

    try {
      const fullAddress = `${formData?.address}, ${formData?.city || ''}, ${formData?.neighborhood || ''}, Jordan`;
      const result = await geocodeAddress(fullAddress);
      
      onChange('latitude', result?.lat?.toString());
      onChange('longitude', result?.lng?.toString());
      
      // Show success message
      setGeocodeError('');
    } catch (error) {
      setGeocodeError(error?.message || 'فشل في تحديد الموقع');
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleGetCurrentLocation = async () => {
    setIsGettingLocation(true);
    setGeocodeError('');

    try {
      const location = await getCurrentLocation();
      onChange('latitude', location?.lat?.toString());
      onChange('longitude', location?.lng?.toString());

      // Reverse geocode to get address
      if (isGoogleMapsConfigured()) {
        const result = await reverseGeocode(location?.lat, location?.lng);
        if (result?.address) {
          onChange('address', result?.address);
        }
        if (result?.neighborhood) {
          onChange('neighborhood', result?.neighborhood);
        }
      }
    } catch (error) {
      setGeocodeError(error?.message || 'فشل في تحديد الموقع الحالي');
    } finally {
      setIsGettingLocation(false);
    }
  };

  const renderCustomField = (field) => {
    const value = formData?.customFields?.[field?.id] || '';
    const error = errors?.[`customFields.${field?.id}`];

    switch (field?.type) {
      case 'textarea':
        return (
          <div key={field?.id} className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              {field?.label} {field?.required && <span className="text-error">*</span>}
            </label>
            <textarea
              className="w-full min-h-24 p-3 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={`أدخل ${field?.label}`}
              value={value}
              onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.value)}
            />
            {error && <p className="text-sm text-error mt-1">{error}</p>}
          </div>
        );
      default:
        return (
          <Input
            key={field?.id}
            label={field?.label}
            type={field?.type || 'text'}
            placeholder={`أدخل ${field?.label}`}
            value={value}
            onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.value)}
            error={error}
            required={field?.required}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <Input
          label="العنوان الكامل"
          type="text"
          placeholder="أدخل العنوان الكامل"
          value={formData?.address}
          onChange={(e) => onChange('address', e?.target?.value)}
          onBlur={() => onBlur?.('address')}
          error={errors?.address}
          required
        />

        <Select
          label="المدينة"
          options={cities}
          value={formData?.city}
          onChange={(value) => onChange('city', value)}
          error={errors?.city}
          required
          searchable
        />

        <Input
          label="الحي"
          type="text"
          placeholder="أدخل اسم الحي"
          value={formData?.neighborhood}
          onChange={(e) => onChange('neighborhood', e?.target?.value)}
          error={errors?.neighborhood}
          required
        />

        <Input
          label="الرمز البريدي"
          type="text"
          placeholder="12345"
          value={formData?.postalCode}
          onChange={(e) => onChange('postalCode', e?.target?.value)}
        />

        <Input
          label="خط العرض"
          type="number"
          step="any"
          placeholder="31.9539"
          value={formData?.latitude}
          onChange={(e) => onChange('latitude', e?.target?.value)}
          onBlur={() => onBlur?.('latitude')}
          description="يتم تحديده تلقائياً من الخريطة"
        />

        <Input
          label="خط الطول"
          type="number"
          step="any"
          placeholder="35.9106"
          value={formData?.longitude}
          onChange={(e) => onChange('longitude', e?.target?.value)}
          onBlur={() => onBlur?.('longitude')}
          description="يتم تحديده تلقائياً من الخريطة"
        />
      </div>

      {/* Geocoding Actions */}
      {isGoogleMapsConfigured() && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleGeocodeAddress}
            disabled={isGeocoding || !formData?.address}
            className="flex items-center gap-2"
          >
            {isGeocoding ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                جاري التحديد...
              </>
            ) : (
              <>
                <Icon name="MapPin" size={16} />
                تحديد الموقع من العنوان
              </>
            )}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleGetCurrentLocation}
            disabled={isGettingLocation}
            className="flex items-center gap-2"
          >
            {isGettingLocation ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                جاري التحديد...
              </>
            ) : (
              <>
                <Icon name="Navigation" size={16} />
                استخدام موقعي الحالي
              </>
            )}
          </Button>
        </div>
      )}

      {geocodeError && (
        <div className="p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm flex items-start gap-2">
          <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
          <span>{geocodeError}</span>
        </div>
      )}

      {/* Interactive Map */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          تحديد الموقع على الخريطة
        </label>
        <div className="rounded-lg overflow-hidden border border-border">
          <GoogleMapComponent
            latitude={formData?.latitude || 31.9454}
            longitude={formData?.longitude || 35.9284}
            onLocationSelect={handleLocationSelect}
            draggable={true}
            zoom={14}
            height="400px"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          انقر على الخريطة أو اسحب العلامة لتحديد الموقع الدقيق
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          ملاحظات الموقع
        </label>
        <textarea
          className="w-full min-h-20 p-3 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="أي ملاحظات أو تفاصيل إضافية عن الموقع..."
          value={formData?.locationNotes}
          onChange={(e) => onChange('locationNotes', e?.target?.value)}
        />
      </div>
    </div>
  );
};

export default LocationTab;